<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: PersonHeader                                          |
| Section: components                                              |
| Purpose: Header block showing person identity and summary facts. |
+------------------------------------------------------------------+
-->
<template>
    <div v-if="loading">
        <SkeletonPersonHeader />
    </div>

    <div v-else>
        <header class="wrapper" v-if="person">
            <div class="person_photo" :class="{ 'person_photo--interactive': isInteractive }"
                :style="{ backgroundImage: profileImageBackgroundImage }" :alt="person.firstname"
                @click="openPhotoOverlay">
            </div>
            <div class="header_box">
                <h1>{{ person.firstname }} {{ person.lastname }}</h1>
                <sup v-if="citations.length">[{{ citationNumbers }}]</sup>
            </div>
            <!-- <h1 v-html="customSpacedName"></h1><sup>[1]</sup> -->
            <p class="subtitle" v-if="person.nickname">"{{ person.nickname }}"</p>
            <p class="PersonHeader_years">
                <img :src="genderIconSrc" :alt="genderAltText" />
                {{ formattedYears }}
            </p>
            <p v-if="formattedLastModifiedDate" class="PersonHeader_lastModified">
                {{ $t('personHeader.lastModified', { date: formattedLastModifiedDate }) }}
            </p>

            <div class="post_meta_wrapper">
                <template v-if="isSelf">
                    <p v-if="title">{{ title }}</p>
                    <span v-if="title">&bull;</span>
                    <!-- <a v-for="(haplogroup, index) in haplogroups" :key="index" :href="generateHaplogroupUrl(haplogroup)"
                        target="_blank" rel="noopener noreferrer">
                        {{ haplogroup.label }} {{ haplogroup.value }}
                    </a> -->
                    <!-- <div class="post_meta post_meta--dna"
                        v-if="dnaNotes.length && !['I3', 'I43'].includes(this.$route.params.id)">
                        DNA-match <img src="@/assets/icons/dropdown_white.svg">
                        <div class="tooltip">
                            <ul>
                                <li v-for="(note, index) in formattedDnaNotes" :key="index" v-html="note"></li>
                            </ul>
                        </div>
                    </div>
                    <div class="post_meta post_meta--dna" v-if="dnaEstimate">
                        {{ dnaEstimate.title }} <img src="@/assets/icons/dropdown_white.svg">
                        <div class="tooltip">
                            <table class="dna-table">
                                <colgroup>
                                    <col style="width: 75%;">
                                    <col style="width: 25%;">
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>{{ person.firstname }} {{ person.lastname }}</th>
                                        <th>100.00%</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(label, index) in dnaEstimate.chartData.labels" :key="index">
                                        <td>{{ label }}</td>
                                        <td>{{ dnaEstimate.chartData.values[index].toFixed(2) }}%</td>
                                    </tr>
                                </tbody>
                            </table>
                            <p class="dna-comment" v-if="dnaEstimate.comment">{{ dnaEstimate.comment }}</p>
                        </div>
                    </div> -->
                </template>
                <template v-else>
                    <p v-if="title">{{ $i18n.locale === 'id' ? `${title}nya Jeffrey` : `Jeffrey's ${title}` }}</p>
                    <span v-if="title">&bull;</span>
                    <p v-if="title">
                        {{ isParentOfSelf && generations === 1 ? $t('personHeader.generationsAgo.one', {
                            n: generations
                        }) : $t('personHeader.generationsAgo.other', { n: generations }) }}
                    </p>
                    <!-- &bull; -->
                    <!-- <a v-for="(haplogroup, index) in haplogroups" :key="index" :href="generateHaplogroupUrl(haplogroup)"
                        target="_blank" rel="noopener noreferrer">
                        {{ haplogroup.label }} {{ haplogroup.value }}
                    </a> -->
                    <!-- <a class="post_meta" v-for="(haplogroup, index) in haplogroups" :key="index"
                        :class="`post_meta--${haplogroup.type}`" :href="generateHaplogroupUrl(haplogroup)"
                        target="_blank" rel="noopener noreferrer">
                        {{ haplogroup.label }} {{ haplogroup.value }} <img src="@/assets/icons/external.svg">
                    </a><span
                        v-if="dnaNotes.length && !['I3', 'I43'].includes(this.$route.params.id)">&bull;</span> -->
                    <!-- <div class="post_meta post_meta--dna"
                        v-if="dnaNotes.length && !['I3', 'I43'].includes(this.$route.params.id)">
                        DNA-match <img src="@/assets/icons/dropdown_white.svg">
                        <div class="tooltip">
                            <ul>
                                <li v-for="(note, index) in formattedDnaNotes" :key="index" v-html="note"></li>
                            </ul>
                        </div>
                    </div><span v-if="dnaEstimate">&bull;</span>
                    <div class="post_meta post_meta--dna" v-if="dnaEstimate">
                        {{ dnaEstimate.title }} <img src="@/assets/icons/dropdown_white.svg">
                        <div class="tooltip">
                            <table class="dna-table">
                                <colgroup>
                                    <col style="width: 75%;">
                                    <col style="width: 25%;">
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>{{ person.firstname }} {{ person.lastname }}</th>
                                        <th>100.00%</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(label, index) in dnaEstimate.chartData.labels" :key="index">
                                        <td>{{ label }}</td>
                                        <td>{{ dnaEstimate.chartData.values[index].toFixed(2) }}%</td>
                                    </tr>
                                </tbody>
                            </table>
                            <p class="dna-comment" v-if="dnaEstimate.comment">{{ dnaEstimate.comment }}</p>
                        </div>
                    </div> -->
                </template>
            </div>

            <BtnSecondary class="BtnSecondary" :label="$t('personHeader.backButton')" url="/search" />
            <BtnSecondary class="BtnSecondary BtnSecondary--print" :label="$t('personHeader.printButton')"
                :url="printUrl" target="_blank" :hideLeftIcon="true" iconRight="fa-solid fa-print" />

            <!-- Add MediaOverlay here -->
            <MediaOverlay v-if="showOverlay" :isVisible="showOverlay" :mediaUrl="overlayMedia?.url"
                :mediaType="overlayMedia?.type" :mediaKind="overlayMedia?.kind" :mediaTitle="overlayMedia?.title"
                :mediaOwner="overlayMedia?.owner"
                :linkedPersonIDs="overlayMedia?.linkedPersonIDs"
                @close="closeOverlay" />
        </header>
    </div>
</template>

<script>
import { isDirectAncestor, getPersonById, getPersonDefaultPhoto, getPersonDnaEstimate, getPersonHaplogroup, getAutosomalDNA } from '@/api/personService';
import { getCitations } from '@/api/personService.js';
import MediaOverlay from '@/components/MediaOverlay.vue';
import BtnSecondary from '@/atoms/BtnSecondary.vue';
import SkeletonPersonHeader from './Skeletons/SkeletonPersonHeader.vue';
import { getBackgroundImageUrl, getPhotoPath as getPhotoPathUtil, normalizeOverlayMedia } from '@/utils/mediaUtils';
import { debugLog } from '@/utils/logger';
// import { PieChart } from 'vue-chart-3';
// import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement, PieController } from 'chart.js';

// ChartJS.register(Title, Tooltip, Legend, ArcElement, PieController);

export default {
    name: 'PersonHeader',
    components: {
        BtnSecondary,
        MediaOverlay,
        SkeletonPersonHeader,
        // PieChart
    },
    data() {
        return {
            person: null,
            ancestorInfo: null, // New data property to store ancestor information
            generations: null,   // New data property for number of generations
            mtdna_haplogroup: null, // mtDNA Haplogroup
            ydna_haplogroup: null,  // Y-DNA Haplogroup
            dnaNotes: [],
            dnaEstimate: null,
            showOverlay: false,
            overlayMedia: null,
            citations: [],  // Add this property to store fetched citations
            profilePhotoMetadata: null,
            loading: true,
        };
    },
    computed: {
        haplogroups() {
            const haplogroups = [];
            if (this.ydna_haplogroup) {
                haplogroups.push({
                    type: 'ydna',
                    value: this.ydna_haplogroup,
                    label: 'Y-DNA:'
                });
            }
            if (this.mtdna_haplogroup) {
                haplogroups.push({
                    type: 'mtdna',
                    value: this.mtdna_haplogroup,
                    label: 'mtDNA:'
                });
            }
            return haplogroups;
        },
        formattedDnaNotes() {
            return this.dnaNotes.map(note =>
                note.replace(/(\r\n|\n|\r)/g, '<br>') // Replace line breaks with <br> tags
            );
        },
        citationNumbers() {
            // Return the total number of citations
            return this.citations.length;
        },
        title() {
            if (!this.ancestorInfo || !this.ancestorInfo.isDirectAncestor) return '';
            const locale = this.$i18n.locale;
            const titles = this.ancestorInfo.titles;
            if (titles && titles[locale]) return titles[locale];
            return this.ancestorInfo.title;
        },
        isSelf() {
            // Check if the current personID matches I43
            return this.$route.params.id === 'I43';
        },
        isParentOfSelf() {
            // Assuming you know the parent IDs of I43, replace 'PARENT_ID_1' and 'PARENT_ID_2' with the actual IDs
            return this.$route.params.id === 'I3' || this.$route.params.id === 'I42';
        },
        customSpacedName() {
            if (!this.person) return '';
            return this.applyCustomLetterSpacing(`${this.person.firstname} ${this.person.lastname}`);
        },
        backUrl() {
            const searchQuery = this.$route.query.q;
            const lastSearchQuery = localStorage.getItem('lastSearchQuery');

            if (searchQuery) {
                return `/search?q=${encodeURIComponent(searchQuery)}`;
            } else if (lastSearchQuery) {
                return `/search?q=${encodeURIComponent(lastSearchQuery)}`;
            } else if (this.person && this.person.lastname) {
                return `/search?q=${encodeURIComponent(this.person.lastname)}`;
            } else {
                return `/search`;
            }
        },
        backButtonLabel() {
            const searchQuery = this.$route.query.q;
            const lastSearchQuery = localStorage.getItem('lastSearchQuery');

            if (!searchQuery && !lastSearchQuery && (!this.person || !this.person.lastname)) {
                return 'Zoekresultaten';
            } else if (searchQuery || lastSearchQuery) {
                return 'Zoekresultaten';
            } else {
                return this.person.lastname;
            }
        },
        printUrl() {
            const route = this.$router.resolve({
                path: this.$route.path,
                query: {
                    ...this.$route.query,
                    print: 'true'
                }
            });

            return route.href;
        },
        genderIconSrc() {
            if (!this.person) return '';
            return this.person.sex === 'M'
                ? require('@/assets/icons/male.svg')
                : require('@/assets/icons/female.svg');
        },
        genderAltText() {
            if (!this.person) return '';
            return this.person.sex === 'M' ? this.$t('personHeader.gender.male') : this.$t('personHeader.gender.female');
        },
        formattedYears() {
            if (!this.person) return '';

            const { birthdatetr, altbirthdatetr, deathdatetr, burialdatetr, living } = this.person;

            // Extract only the year from a date string, ignoring month and day if they are "00"
            const extractYear = (date) => {
                if (!date || date.startsWith("0000")) return null;
                const [year] = date.split('-');
                // Only return the year if it is not "0000"
                return year && year !== '0000' ? year : null;
            };

            // Get birth and death years
            const finalBirthYear = extractYear(birthdatetr) || extractYear(altbirthdatetr);
            const finalDeathYear = extractYear(deathdatetr) || extractYear(burialdatetr);

            // Debugging: Output the extracted values
            debugLog('Birth Year:', finalBirthYear);
            debugLog('Death Year:', finalDeathYear);
            debugLog('Living Status:', living);

            // If both birth and death years are known (even partial), display them as a range
            if (finalBirthYear && finalDeathYear) {
                const lifespan = parseInt(finalDeathYear) - parseInt(finalBirthYear);
                debugLog('Lifespan Calculation:', lifespan);
                return this.$t('personHeader.years.lifespan', { birth: finalBirthYear, death: finalDeathYear, years: lifespan });
            }

            // If only a birth year is known and no death year is available, or the person is still alive
            if (finalBirthYear && !finalDeathYear) {
                if (living === 1) {
                    debugLog('Person is living with known birth year');
                    return this.$t('personHeader.years.living', { birth: finalBirthYear });
                } else {
                    debugLog('Only birth year known, person not living');
                    return this.$t('personHeader.years.bornIn', { year: finalBirthYear });
                }
            }

            // If only the death year is known
            if (!finalBirthYear && finalDeathYear) {
                debugLog('Only death year known');
                return this.$t('personHeader.years.diedIn', { year: finalDeathYear });
            }

            // If no birth and death dates are known
            if (!finalBirthYear && !finalDeathYear) {
                debugLog('No birth or death years known');
                return living === 1 ? this.$t('personHeader.years.alive') : this.$t('personHeader.years.deceased');
            }

            // Fallback message
            debugLog('Fallback: No birth or death dates available');
            return this.$t('personHeader.years.unknown');
        },
        formattedLastModifiedDate() {
            const match = String(this.person?.changedate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (!match || match[1] === '0000' || match[2] === '00' || match[3] === '00') return '';

            const year = Number(match[1]);
            const month = Number(match[2]);
            const day = Number(match[3]);
            const date = new Date(Date.UTC(year, month - 1, day));

            if (Number.isNaN(date.getTime())
                || date.getUTCFullYear() !== year
                || date.getUTCMonth() !== month - 1
                || date.getUTCDate() !== day) return '';

            const localeMap = { nl: 'nl-NL', en: 'en-GB', id: 'id-ID' };
            return new Intl.DateTimeFormat(localeMap[this.$i18n.locale] || localeMap.nl, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                timeZone: 'UTC',
            }).format(date);
        },
        profileImageSrc() {
            if (!this.person) return ''; // If no person data, return an empty string
            if (this.person.profileImage) {
                return this.person.profileImage; // Return the profile image if available
            } else {
                // Return the default image based on gender
                return this.person.sex === 'M'
                    ? require('@/assets/defaults/default_photo_male.svg')
                    : require('@/assets/defaults/default_photo_female.svg');
            }
        },
        profileImageBackgroundImage() {
            return getBackgroundImageUrl(this.profileImageSrc);
        },
        isInteractive() {
            return !!this.person?.profileImage; // Returns true if a custom profile image is available
        }
    },
    methods: {
        generateHaplogroupUrl(haplogroup) {
            const baseUrl = 'https://scaledinnovation.com/gg/snpTracker.html?snp=';
            if (haplogroup.type === 'mtdna') {
                return `${baseUrl}${haplogroup.value}&mt`; // Add '&mt' for mtDNA haplogroups
            }
            return `${baseUrl}${haplogroup.value}`; // Standard URL for Y-DNA haplogroups
        },
        // applyCustomLetterSpacing(name) {
        //     const letterSpacingMap = {
        //         'a': 'custom_a',
        //         'l': 'custom_l',
        //         's': 'custom_s',
        //         't': 'custom_t'
        //         // Voeg meer mappings toe indien nodig
        //     };

        //     let processedHTML = '';

        //     for (let i = 0; i < name.length; i++) {
        //         const char = name[i];
        //         const lowerChar = char.toLowerCase();
        //         if (letterSpacingMap[lowerChar]) {
        //             processedHTML += `<span class="${letterSpacingMap[lowerChar]}">${char}</span>`;
        //         } else {
        //             processedHTML += char;
        //         }
        //     }

        //     return processedHTML;
        // },
        openPhotoOverlay() {
            if (!this.isInteractive) return;

            this.overlayMedia = normalizeOverlayMedia({
                ...this.profilePhotoMetadata,
                url: this.profileImageSrc,
                title: `${this.person.firstname} ${this.person.lastname}`
            }, {
                type: 'image',
                kind: 'photo'
            });
            this.showOverlay = true;

            debugLog('Photo overlay opened:', {
                overlayMedia: this.overlayMedia
            });
        },
        closeOverlay() {
            this.showOverlay = false;
            debugLog('Photo overlay closed');
        }
    },
    async created() {
        this.loading = true;
        const personID = this.$route.params.id;

        // Fetch person data
        try {
            const data = await getPersonById(personID);

            // Extract only the year from the date strings
            const extractYear = (dateString) => {
                if (!dateString || dateString === '0000-00-00') return null; // Handle invalid or missing dates
                const date = new Date(dateString); // Convert to a Date object
                return date instanceof Date && !isNaN(date) ? date.getFullYear().toString() : null; // Extract year
            };

            this.person = {
                ...data,
                birthYear: extractYear(data.birthdatetr),
                altBirthYear: extractYear(data.altbirthdatetr),
                deathYear: extractYear(data.deathdatetr),
                burialYear: extractYear(data.burialdatetr),
                living: data.living,
                nickname: data.nickname
            };
        } catch (error) {
            console.error('Error fetching person data:', error);
            this.loading = false;
            this.$emit('loaded');
            return;
        }

        // Fetch citations
        try {
            const citationsResponse = await getCitations(personID);
            this.citations = citationsResponse || []; // Assign the fetched citations
        } catch (error) {
            console.error('Error fetching citations:', error);
        }

        // Fetch haplogroup data
        try {
            const haplogroupResponse = await getPersonHaplogroup(personID);

            if (haplogroupResponse.length > 0) {
                const haplogroupData = haplogroupResponse[0];
                this.mtdna_haplogroup = haplogroupData.mtdna_haplogroup || null;
                this.ydna_haplogroup = haplogroupData.ydna_haplogroup || null;
            }
        } catch (error) {
            console.error('Error fetching haplogroup data:', error);
        }

        // Fetch autosomal DNA data
        try {
            const dnaResponse = await getAutosomalDNA(personID);
            this.dnaNotes = dnaResponse; // Assuming it returns an array of notes
        } catch (error) {
            console.error('Error fetching atDNA data:', error);
        }

        // Match personID with DNA estimate
        try {
            const estimate = await getPersonDnaEstimate(personID);
            if (estimate) {
                this.dnaEstimate = estimate;
            }
        } catch (error) {
            console.error('Error fetching dnaEstimate data:', error);
        }

        // Fetch default profile photo
        try {
            const photoData = await getPersonDefaultPhoto(personID);
            if (photoData) {
                this.person.profileImage = getPhotoPathUtil(photoData.path);
                this.profilePhotoMetadata = {
                    owner: photoData.owner || '',
                    linkedPersonIDs: photoData.linkedPersonIDs || []
                };
            } else {
                this.person.profileImage = 'default-profile-image-path'; // Fallback image
            }
        } catch (error) {
            console.error('Error fetching profile image:', error);
        }

        // Fetch ancestor information
        try {
            const ancestorResponse = await isDirectAncestor(personID);
            this.ancestorInfo = ancestorResponse;
            this.generations = ancestorResponse.generations; // Store the generations count
        } catch (error) {
            console.error('Error fetching ancestor info:', error);
        }

        // Log to verify the parsed data
        debugLog('Parsed Person Data:', this.person);

        this.loading = false;
        this.$emit('loaded');
    }
}
</script>

<style scoped>
h1 {
    text-align: center;
}

sup {
    font-family: var(--font-primary);
    font-size: 16px;
    color: var(--brand-color-1);
}

.header_box {
    display: inline-flex;
    flex-direction: row;
    gap: 8px;
    align-items: flex-start;
    justify-content: flex-start;
}

header {
    position: relative;
    margin-top: 72px;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    padding: 0 20px;
}

.person_photo {
    width: 130px;
    height: 130px;
    flex-shrink: 0;
    border-radius: var(--border-radius-pill);
    /* border: 2px solid var(--brand-text-dark); */
    background-size: 105%;
    background-repeat: no-repeat;
    background-position: center;
    margin-bottom: 36px;
    box-shadow: var(--shadow-photo);
}

.person_photo--interactive {
    cursor: pointer;
    transition: var(--transition-bounce);
}

.person_photo--interactive:hover {
    background-size: 115%;
}

.PersonHeader_years {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 24px;
    margin-top: 8px;
}

.PersonHeader_years img {
    height: 18px;
}

.PersonHeader_lastModified {
    margin-top: 24px;
    color: var(--brand-text-dark);
    font-size: 14px;
    line-height: 1.4;
    text-align: center;
    opacity: 0.65;
}

.BtnSecondary {
    position: absolute;
    left: 20px;
    top: 0;
}

.BtnSecondary--print {
    left: auto;
    right: 20px;
}

.post_meta_wrapper {
    margin-top: 16px;
    color: var(--brand-text-dark);
    font-size: 14px;
}

.post_meta_wrapper p,
.post_meta_wrapper a {
    color: var(--brand-text-dark);
    font-size: 14px;
}

.subtitle {
    margin-top: 8px;
    margin-bottom: 16px;
}

.post_meta--ydna {
    background-color: #3595d6;
    color: var(--brand-text-light);
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 0px #3595d6;
}

.post_meta--ydna:hover {
    box-shadow: 0px 0px 0px 4px #3595d6;
    text-decoration: underline;
    border-radius: 2px;
}

.post_meta--mtdna {
    background-color: #ed5e90;
    color: var(--brand-text-light);
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 0px #ed5e90;
}

.post_meta--mtdna:hover {
    box-shadow: 0px 0px 0px 4px #ed5e90;
    text-decoration: underline;
    border-radius: 2px;
}

.post_meta--dna {
    background-color: var(--brand-color-1);
    color: var(--brand-text-light);
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 0px var(--brand-color-1);
    position: relative;
    display: flex;
    flex-direction: row;
    gap: 6px;
}

.post_meta--dna img {
    width: 8px;
    transition: var(--transition-ease);
}

.post_meta--dna:hover {
    box-shadow: 0px 0px 0px 4px var(--brand-color-1);
    text-decoration: underline;
    border-radius: 2px;
    cursor: pointer;
}

.post_meta--dna:hover img {
    transform: rotate(-180deg);
}

.post_meta--dna:hover .tooltip {
    visibility: visible;
    opacity: 1;
}

.tooltip {
    visibility: hidden;
    opacity: 0;
    /* visibility: hidden; */
    /* opacity: 0; */
    box-shadow: var(--shadow-card-sm);
    margin-top: 0px;
    padding: 36px;
    width: max-content;
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
    overflow: hidden;
}

.dna-comment {
    margin-top: 8px;
    font-size: 12px;
    opacity: 0.5;
    font-weight: 400;
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
}

.tooltip,
.dna-comment {
    box-sizing: border-box;
}

.dna-table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
    font-size: 14px;
    color: var(--text-color);
}

.dna-table th {
    padding-bottom: 8px;
    background-color: var(--brand-bg-light);
    font-weight: bold;
    text-align: left;
}

.dna-table td {
    padding: 8px 0;
    border-top: 1px solid #eee;
    border-bottom: 1px solid #eee;
    font-weight: 400;
}

.dna-table tr th:nth-of-type(2),
.dna-table tr td:nth-of-type(2) {
    text-align: right;
    font-variant-numeric: tabular-nums;
    /* Use monospaced numbers */
    padding-left: 8px;
}

.dna-comment {
    margin-top: 8px;
    font-size: 12px;
    opacity: .5;
    font-weight: 400;
}

@media (max-width: 860px) {
    .person_photo {
        width: 96px;
        height: 96px;
    }

    .PersonHeader_years {
        font-size: 18px;
    }
}
</style>
